import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography
} from '@mui/material';
import DashboardCard from '../components/DashboardCard';
import {
    GoogleMap,
    Marker,
    InfoWindow
} from "@react-google-maps/api";
import { useSelector } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import Chart from 'react-apexcharts';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import { colors } from '../components/Theme/WebTheme';

const Dashboard = () => {
    const [mylocation, setMylocation] = useState(null);
    const [locations, setLocations] = useState([]);
    const [dailygross, setDailygross] = useState(0);
    const [monthlygross, setMonthlygross] = useState(0);
    const [totalgross, setTotalgross] = useState(0);

    const [settings, setSettings] = useState({});
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const usersdata = useSelector(state => state.usersdata);
    const zonelistdata = useSelector(state => state.zonelistdata);
    const bookinglistdata = useSelector(state => state.bookinglistdata);
    const settingsdata = useSelector(state => state.settingsdata);
    const auth = useSelector(state => state.auth);
    const cars = useSelector(state => state.cartypes.cars);
    const [allCompleteCount, setAllCompleteCount] = useState([]);
    const [allCancelCount, setAllCancelCount] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [driverCount, setDriverCount] = useState(0);
    const [customerCount, setCustomerCount] = useState(0);
    const [pendingDispatch, setPendingDispatch] = useState({ count: 0, nextIn: null });
    const [zoneDrivers, setZoneDrivers] = useState({});
    useEffect(() => {
        if (mylocation == null) {
            navigator.geolocation.getCurrentPosition(
                position => setMylocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                }),
                error => console.log(error)
            );
        }
    }, [mylocation]);

    useEffect(() => {
        if (settingsdata.settings) {
            setSettings(settingsdata.settings);
        }
    }, [settingsdata.settings]);

    useEffect(() => {
        if (usersdata.users) {
            const drivers = usersdata.users.filter(user => user.usertype === 'driver' && ((user.fleetadmin === auth.profile.uid && auth.profile.usertype === 'fleetadmin') || auth.profile.usertype === 'admin'));
            let locs = [];
            for (let i = 0; i < drivers.length; i++) {
                if (drivers[i].approved && drivers[i].driverActiveStatus && drivers[i].location && cars) {
                    let carImage;
                    for (let j = 0; j < cars.length; j++) {
                        if (cars[j].name === drivers[i].carType) {
                            carImage = cars[j].image;
                        }
                    }
                    locs.push({
                        id: i,
                        lat: drivers[i].location.lat,
                        lng: drivers[i].location.lng,
                        drivername: drivers[i].firstName + ' ' + drivers[i].lastName,
                        carnumber: drivers[i].vehicleNumber,
                        cartype: drivers[i].carType,
                        carImage: carImage
                    });
                }
            }
            setLocations(locs);
        }
    }, [usersdata.users, auth.profile.usertype, auth.profile.uid, cars]);

    // Zones widget grouping (admin/fleet scope)
    const pip = (lat, lng, poly) => {
      if (!poly || poly.length < 3) return false;
      const toLngLatA = (p) => Array.isArray(p) ? [Number(p[1]), Number(p[0])] : [Number(p.lng), Number(p.lat)];
      const toLngLatB = (p) => Array.isArray(p) ? [Number(p[0]), Number(p[1])] : [Number(p.lng), Number(p.lat)];
      const test = (mapFn) => {
        let inside = false;
        for (let i=0,j=poly.length-1; i<poly.length; j=i++){
          const [x1,y1] = mapFn(poly[i]);
          const [x2,y2] = mapFn(poly[j]);
          if (Number.isNaN(x1) || Number.isNaN(y1) || Number.isNaN(x2) || Number.isNaN(y2)) continue;
          const intersect = ((y1>lat)!==(y2>lat)) && (lng < (x2 - x1) * (lat - y1) / ((y2 - y1) || 1e-9) + x1);
          if (intersect) inside = !inside;
        }
        return inside;
      };
      return test(toLngLatA) || test(toLngLatB);
    };
    useEffect(() => {
      if (!usersdata.users || !zonelistdata.zones) return;
      const drivers = usersdata.users.filter(u=>u.usertype==='driver' && u.driverActiveStatus && u.location && ((auth.profile.usertype==='admin') || (auth.profile.usertype==='fleetadmin' && u.fleetadmin===auth.profile.uid)));
      const zones = zonelistdata.zones.filter(z=>z.active && Array.isArray(z.zone_path) && z.zone_path.length>=3);
      const groups = {};
      zones.forEach(z=>{ groups[z.zone_name] = []; });
      drivers.forEach(d=>{
        const { lat, lng } = d.location;
        for (let z of zones){
          if (pip(lat, lng, z.zone_path)) { groups[z.zone_name].push(d); break; }
        }
      });
      setZoneDrivers(groups);
    }, [usersdata.users, zonelistdata.zones, auth.profile.usertype, auth.profile.uid]);

    useEffect(() => {
        if (bookinglistdata.bookings) {
            let today = new Date();
            let monthlyTotal = 0;
            let yearlyTotal = 0;
            let todayTotal = 0;
            for (let i = 0; i < bookinglistdata.bookings.length; i++) {
                if ((bookinglistdata.bookings[i].status === 'PAID' || bookinglistdata.bookings[i].status === 'COMPLETE') && ((bookinglistdata.bookings[i].fleetadmin === auth.profile.uid && auth.profile.usertype === 'fleetadmin')|| auth.profile.usertype === 'admin') ) {
                    const { tripdate, convenience_fees, driver_share, discount } = bookinglistdata.bookings[i];
                    let tDate = new Date(tripdate);
                    if (convenience_fees && parseFloat(convenience_fees) > 0 && auth.profile.usertype === 'admin') {
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(convenience_fees) - parseFloat(discount);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
                    }
                    if (driver_share && parseFloat(driver_share) > 0 && auth.profile.usertype === 'fleetadmin'){
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(driver_share);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(driver_share);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(driver_share);
                    }
                }
            }
            // pending dispatch queue summary
            const now = Date.now();
            const pending = bookinglistdata.bookings.filter(b => b.status==='NEW' && b.dispatchQueue && b.dispatchQueue.length>0);
            let nextIn = null;
            pending.forEach(b => {
                if (b.nextDispatchAt && b.nextDispatchAt > now) {
                    const diff = Math.ceil((b.nextDispatchAt - now)/1000);
                    if (nextIn===null || diff < nextIn) nextIn = diff;
                }
            });
            setPendingDispatch({ count: pending.length, nextIn });

            setDailygross(parseFloat(todayTotal).toFixed(settings.decimal));
            setMonthlygross(parseFloat(monthlyTotal).toFixed(settings.decimal));
            setTotalgross(parseFloat(yearlyTotal).toFixed(settings.decimal));
        }
    }, [bookinglistdata.bookings, settings,auth.profile.uid,auth.profile.usertype]);

    const barChartOptions = {
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        colors: [colors.PRIMARY || "#2563EB", colors.ACCENT_SOLID || "#0FEEBC"],
        options: {
            maintainAspectRatio: false,
            scales: {
                yAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                        },
                    },
                ],
            },
            chart: {
                height: 350,
                type: 'area'
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                curve: 'smooth'
            },
        },
    };

    //Completed Bookings and Cancelled Bookings

    const barChart = [{
        name: t('completed_bookings'),
        data: allCompleteCount
    }, {
        name: t('cancelled_bookings'),
        data: allCancelCount
    }];
    useEffect(() => {
        if (usersdata.users) {
            let driverCount = 0;
            let activeCount = 0;
            let customerCount = 0;
            for (let i = 0; i < usersdata.users.length; i++) {
                if (usersdata.users[i].usertype === 'driver') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        driverCount = driverCount + 1;
                    }
                }
                if (usersdata.users[i].driverActiveStatus === true) {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        activeCount = activeCount + 1;
                    }
                }
                if (usersdata.users[i].usertype === 'customer') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        customerCount = customerCount + 1;
                    }
                }
            }
            setActiveCount(activeCount);
            setDriverCount(driverCount);
            setCustomerCount(customerCount);
        } else {
            setActiveCount(0)
            setDriverCount(0)
            setCustomerCount(0)
        }
    }, [usersdata.users, auth.profile]);

    useEffect(() => {
        let allCompleteCount = [];
        let allCancelCount = [];
        let months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        if (bookinglistdata.bookings) {
            for (let i = 0; i < months.length; i++) {
                let completeCount = 0;
                let cancelCount = 0;
                for (let j = 0; j < bookinglistdata.bookings.length; j++) {
                    const { tripdate } = bookinglistdata.bookings[j];
                    let tDate = new Date(tripdate);
                    if (months[i] === months[tDate.getMonth()] && (bookinglistdata.bookings[j].status === 'PAID' || bookinglistdata.bookings[j].status === 'COMPLETE')) {
                        completeCount = completeCount + 1;
                    }
                    if (months[i] === months[tDate.getMonth()] && bookinglistdata.bookings[j].status === 'CANCELLED') {
                        cancelCount = cancelCount + 1;
                    }
                }
                allCompleteCount.push(completeCount);
                allCancelCount.push(cancelCount);
            }
        }
        setAllCompleteCount(allCompleteCount);
        setAllCancelCount(allCancelCount);
    }, [bookinglistdata.bookings]);



    // const onLoad = React.useCallback(function callback(map) {
    //   const bounds = new window.google.maps.LatLngBounds(mylocation);
    //   map.fitBounds(bounds);
    // }, [mylocation])
  
    // const onUnmount = React.useCallback(function callback() {

    // }, [])

    return (
        bookinglistdata.loading || usersdata.loading ? <CircularLoading /> :
            <div>
                <Typography variant="h4" style={{ margin: "20px 20px 0 15px", textAlign: isRTL === 'rtl' ? 'right' : 'left', fontWeight: 700, color: colors.DARK || '#111827' }}>{t('gross_earning')}</Typography>
                <Grid container direction="row" spacing={2}>
                    <Grid item xs style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left', }}>
                        {settings.swipe_symbol === false ?
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: 16, background: `linear-gradient(135deg, ${colors.PRIMARY || '#2563EB'} 0%, #3B82F6 100%)`, boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }} title={t('today_text')} image={require("../assets/img/money1.jpg").default}>{settings.symbol + ' ' + dailygross}</DashboardCard>
                            :
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: 16, background: `linear-gradient(135deg, ${colors.PRIMARY || '#2563EB'} 0%, #3B82F6 100%)`, boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }} title={t('today_text')} image={require("../assets/img/money1.jpg").default}>{dailygross + ' ' + settings.symbol}</DashboardCard>
                        }
                    </Grid>
                    <Grid item xs style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
                        {settings.swipe_symbol === false ?
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: 16, background: `linear-gradient(135deg, ${colors.ACCENT_SOLID || '#0FEEBC'} 0%, #10B981 100%)`, boxShadow: '0 4px 12px rgba(15,238,188,0.3)' }} title={t('this_month_text')} image={require("../assets/img/money2.jpg").default}>{settings.symbol + ' ' + monthlygross}</DashboardCard>
                            :
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: 16, background: `linear-gradient(135deg, ${colors.ACCENT_SOLID || '#0FEEBC'} 0%, #10B981 100%)`, boxShadow: '0 4px 12px rgba(15,238,188,0.3)' }} title={t('this_month_text')} image={require("../assets/img/money2.jpg").default}>{monthlygross + ' ' + settings.symbol}</DashboardCard>
                        }
                    </Grid>
                    <Grid item xs style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
                        {settings.swipe_symbol === false ?
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: 16, background: `linear-gradient(135deg, ${colors.DARK || '#111827'} 0%, #374151 100%)`, boxShadow: '0 4px 12px rgba(17,24,39,0.3)' }} title={t('total')} image={require("../assets/img/money3.jpg").default}>{settings.symbol + ' ' + totalgross}</DashboardCard>
                            :
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: 16, background: `linear-gradient(135deg, ${colors.DARK || '#111827'} 0%, #374151 100%)`, boxShadow: '0 4px 12px rgba(17,24,39,0.3)' }} title={t('total')} image={require("../assets/img/money3.jpg").default}>{totalgross + ' ' + settings.symbol}</DashboardCard>
                        }
                    </Grid>
                </Grid>
                
                <Grid item xs={12} style={{ marginTop: 20 }}>
                    <Grid container style={{  direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }} >
                        <Grid item xs={12} sm={12} md={9} lg={9} style={{ backgroundColor: '#fff', borderRadius: 16, overflow: 'hidden', border: `1px solid ${colors.BORDER_BACKGROUND || '#E5E7EB'}` }} boxShadow={1}>
                            <Typography variant="h4" style={{ margin: "20px 20px 0 15px", textAlign: isRTL === 'rtl' ? 'right' : 'left', fontWeight: 600, color: colors.DARK || '#111827' }}>{t('real_time_driver_section_text')}</Typography>
                            {mylocation && mylocation.lat?
                            <GoogleMap
                                zoom={10}
                                center={mylocation}
                                mapContainerStyle={{height: `380px` }}
                                >
                                {locations.map(marker => (
                                    <Marker
                                        position={{ lat: marker.lat, lng: marker.lng }}
                                        key={marker.id}
                                        icon={{
                                            url: marker.carImage,
                                            scaledSize:  new window.google.maps.Size(35,25)
                                        }}

                                    >
                                        <InfoWindow
                                           position={{ lat: marker.lat, lng: marker.lng }} 
                                           options={{ pixelOffset: new window.google.maps.Size(0, -32) }}
                                        >
                                            <div>{marker.drivername}<br/>{marker.carnumber}</div>
                                        </InfoWindow>
                                
                                    </Marker>
                                ))}
                            </GoogleMap>
                            :null}
                        </Grid>
                        <Grid item xs={12} sm={12} md={3} lg={3} style={{ padding: 25, marginTop: '30px' }}>
                            <Card style={{ borderRadius: 16, background: `linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)`, minHeight: 100, marginBottom: 20, boxShadow: '0 4px 12px rgba(139,92,246,0.3)' }}>
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 14, marginTop: 20, fontWeight: 500, opacity: 0.9 }}>{t('total_cumtomer')}</Typography>
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 32, fontWeight: 700, paddingBottom: 16 }}>{customerCount}</Typography>
                            </Card>
                            <Card style={{ borderRadius: 16, background: `linear-gradient(135deg, ${colors.PRIMARY || '#2563EB'} 0%, #3B82F6 100%)`, minHeight: 100, marginBottom: 20, boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }}>
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 14, marginTop: 20, fontWeight: 500, opacity: 0.9 }}>{t('total_drivers')}</Typography>
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 32, fontWeight: 700, paddingBottom: 16 }}>{driverCount}</Typography>
                            </Card>
                            <Card style={{ borderRadius: 16, background: `linear-gradient(135deg, ${colors.ACCENT_SOLID || '#0FEEBC'} 0%, #10B981 100%)`, minHeight: 100, boxShadow: '0 4px 12px rgba(15,238,188,0.3)' }}>
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 14, marginTop: 20, fontWeight: 500, opacity: 0.9 }}>{t('active_driver')}</Typography>
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 32, fontWeight: 700, paddingBottom: 16 }}>{activeCount}</Typography>
                            </Card>
                        </Grid>

                    </Grid>
                </Grid>
                <Box style={{ padding: 24, marginTop: 24, borderRadius: 16, backgroundColor: '#fff', border: `1px solid ${colors.BORDER_BACKGROUND || '#E5E7EB'}` }} boxShadow={1}>
                    {(auth.profile.usertype==='admin' || auth.profile.usertype==='fleetadmin') && (
                        <Grid container spacing={2} sx={{ mb:2 }}>
                            <Grid item xs={12}>
                                <Card sx={{ borderRadius: 2, bgcolor:'#fafafa' }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" sx={{ opacity:0.8, mb:1 }}>{t('drivers_by_zone') || 'Drivers by zone (online)'}</Typography>
                                        <Grid container spacing={2}>
                                          {Object.keys(zoneDrivers).length===0 ? (
                                            <Grid item xs={12}><Typography variant="body2" sx={{ opacity:0.8 }}>{t('no_zones') || 'No active zones'}</Typography></Grid>
                                          ) : (
                                            Object.keys(zoneDrivers).map((zname)=> (
                                              <Grid key={zname} item xs={12} md={4}>
                                                <Box sx={{ p:1.5, border:'1px solid #e7e7e7', borderRadius:2 }}>
                                                  <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight:600 }}>{zname}</Typography>
                                                    <Chip size="small" label={zoneDrivers[zname].length} />
                                                  </Box>
                                                  {/* car type distribution */}
                                                  {(() => {
                                                    const counts = {};
                                                    zoneDrivers[zname].forEach(d => {
                                                      const k = d.carType || '—';
                                                      counts[k] = (counts[k] || 0) + 1;
                                                    });
                                                    const entries = Object.entries(counts);
                                                    return entries.length ? (
                                                      <Box sx={{ mt:0.75, display:'flex', gap:0.5, flexWrap:'wrap' }}>
                                                        {entries.map(([k,v]) => (
                                                          <Chip key={k} size="small" variant="outlined" label={`${k}: ${v}`} />
                                                        ))}
                                                      </Box>
                                                    ) : null;
                                                  })()}
                                                  {zoneDrivers[zname].slice(0,6).map(d=> (
                                                    <Typography key={d.id} variant="caption" display="block">{(d.firstName||'') + ' ' + (d.lastName||'')}</Typography>
                                                  ))}
                                                  {zoneDrivers[zname].length>6 ? (
                                                    <Typography variant="caption" sx={{ opacity:0.7 }}>+{zoneDrivers[zname].length-6} {t('more') || 'more'}</Typography>
                                                  ): null}
                                                </Box>
                                              </Grid>
                                            ))
                                          )}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                    {(auth.profile.usertype==='admin' || auth.profile.usertype==='fleetadmin') && (
                        <Grid container spacing={2} sx={{ mb:2 }}>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ borderRadius: 2, bgcolor:'#fafafa' }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" sx={{ opacity:0.8 }}>{t('pending_offers') || 'Pending offers'}</Typography>
                                        <Typography variant="h5" sx={{ fontWeight:700 }}>{pendingDispatch.count}</Typography>
                                        {pendingDispatch.nextIn!==null ? (
                                            <Typography variant="caption">{t('next_offer_in') || 'Next offer in'} {pendingDispatch.nextIn}s</Typography>
                                        ) : null}
                                        <Box sx={{ mt:1.5 }}>
                                            <Button size="small" variant="outlined" onClick={()=>window.location.assign('/bookings')}>{t('manage') || 'Manage'}</Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ borderRadius: 2, bgcolor:'#fafafa' }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" sx={{ opacity:0.8 }}>{t('test_dispatch') || 'Test dispatch'}</Typography>
                                        <Box sx={{ display:'flex', gap:1, mt:1 }}>
                                            <input id="test-booking-id" placeholder={t('booking_id') || 'Booking ID'} style={{ flex:1, padding:'8px', border:'1px solid #e7e7e7', borderRadius:6 }} />
                                            <Button size="small" variant="contained" onClick={async()=>{
                                                const el = document.getElementById('test-booking-id');
                                                const id = el && el.value ? el.value.trim() : '';
                                                if(!id) return alert('Enter booking ID');
                                                try{
                                                    const res = await fetch('https://us-central1-reaplnprge.cloudfunctions.net/test_dispatch',{
                                                        method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ bookingId: id })
                                                    });
                                                    const json = await res.json();
                                                    alert(JSON.stringify(json));
                                                }catch(e){ alert(e.message); }
                                            }}>{t('run') || 'Run'}</Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                    <Typography style={{margin: "5px 20px 0 15px", color: colors.DARK || '#111827', fontWeight: 600}} variant="h5">{t('booking_chart')}</Typography>
                    <Chart
                        options={barChartOptions}
                        series={barChart}
                        type="area"
                        width="100%"
                        height={360}
                    />
                </Box>
            </div>

    )
}

export default Dashboard;
